import { useServices } from '~/composables/useServices';
import { globalKeys } from 'assets/data/global-keys';
import { useLocale } from '~/composables/useStates/useLocale';

export class CaseInsensitiveMap<T, U> extends Map<T, U> {
  override set(key: T, value: U): this {
    if (typeof key === 'string') {
      key = key.toLowerCase() as never as T;
    }
    return super.set(key, value);
  }

  override get(key: T): U | undefined {
    if (typeof key === 'string') {
      key = key.toLowerCase() as never as T;
    }

    return super.get(key);
  }

  override has(key: T): boolean {
    if (typeof key === 'string') {
      key = key.toLowerCase() as never as T;
    }

    return super.has(key);
  }
}

export default () => {
  //Actions
  const toggleLocaleDebug = (): void => {
    const locale = useLocale();
    locale.value.debug = !locale.value.debug;
  };
  const setLocale = (data: Record<string, string>) => {
    const locale = useLocale();
    locale.value.messagesMap = new CaseInsensitiveMap(Object.entries(data));
  };
  const setLocaleOptions = async (data) => {
    const locale = useLocale();
    const options = [];
    data.forEach((el) => {
      options.push({
        cultureCode: el.cultureCode,
        translation: el.translation,
      });
    });
    locale.value.localeOptions = options;
  };
  const changeLocale = async (newLocale: string) => {
    setLocaleCode(newLocale);
    await fetchLocale(newLocale);
  };
  const fetchLocale = async (newLocale: string) => {
    setLocaleCode(newLocale);
    const { $localeService } = useServices();
    const data = await $localeService.fetchLocale(newLocale);
    if (!!data) {
      setLocale(data);
      return data;
    }
  };
  const setLocaleCode = (newLocale: string) => {
    const locale = useLocale();
    const { $storageService } = useServices();
    $storageService.setCookie({
      key: globalKeys.siteConfigKeys.locale,
      data: locale,
      expiry: 1,
    });
    locale.value.locale = newLocale;
  };

  //Getters
  const getLocale = () => {
    const locale = useLocale();
    return locale.value.locale || 'en-US';
  };
  const debugEnabled = () => {
    const locale = useLocale();
    return locale.value.debug;
  };
  const localeKey = (key: string, alternativeKey?: string) => {
    const locale = useLocale();
    try {
      if (locale.value.debug) {
        console.info({
          key,
          value: locale.value.messagesMap.get(key) || null,
        });
        return `${key} | ${!locale.value.messagesMap.get(key) ? 0 : 1}`;
      }
      if (locale.value.messagesMap.size === 0) {
        const splitKey = key.split('.');
        return splitKey[splitKey.length - 1];
      }
      if (locale.value.messagesMap.size > 0)
        return (
          locale.value.messagesMap.get(key) ||
          locale.value.messagesMap.get(alternativeKey) ||
          key
        );
    } catch (e) {
      console.error({ e });
    }
  };
  const interpolationLocaleKey = (
    key: string,
    params: Record<string, string>,
  ): string => {
    const messageSegments = localeKey(key).split(/%?{([^}]*)}/g);
    const remappedSegments = messageSegments.map((s) => {
      if (typeof params[s] !== 'undefined') return params[s];
      return s;
    });
    return remappedSegments.join('');
  };
  const getLocaleOptions = () => {
    const locale = useLocale();
    return locale.value.localeOptions;
  };
  return {
    toggleLocaleDebug,
    setLocaleOptions,
    setLocale,
    changeLocale,
    fetchLocale,
    setLocaleCode,
    getLocale,
    interpolationLocaleKey,
    debugEnabled,
    localeKey,
    getLocaleOptions,
  };
};
